import React from 'react';
import { Tooltip } from 'antd';
import { TruncatedText } from '@spark';
import { getColorForUsecaseStatus } from '../HomepageTableView';

/**
 * Small colored dot whose color is derived from the usecase status.
 * Shared by `TaskStatusSummary` (composed task row) and `StatusWithDot`
 * (single sub-task row) so the visual is consistent across rows.
 */
export const StatusDot = ({ status, size = 8 }) => (
    <span
        style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: getColorForUsecaseStatus(status),
            display: 'inline-block',
            marginRight: 4,
            verticalAlign: 'middle',
        }}
    />
);

/**
 * Single "<dot> <status>" pill (no count). Used to render a sub-task's
 * status in the List View — matching the composed-task row visual but
 * without the leading count.
 */
export const StatusWithDot = ({ status }) => {
    if (!status) return null;
    return (
        <span
            className="fs-unmask"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
            }}
        >
            <StatusDot status={status} />
            {status}
        </span>
    );
};

/**
 * Tooltip body shown on hover for composed tasks. Mirrors the deployment
 * badge tooltip layout: a header line, then one row per sub-task with a
 * status-colored dot, the sub-task name, and its status.
 */
const StatusBreakdown = ({ summaryText, items }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '2px 0' }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
            {summaryText || 'Status by sub-task'}
        </div>
        {items.map((item, index) => (
            <div
                key={`${item.name}-${index}`}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
                <span
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: getColorForUsecaseStatus(item.status),
                        display: 'inline-block',
                        flexShrink: 0,
                    }}
                />
                <span style={{ fontWeight: 500 }}>{item.name}</span>
                {item.status && <span style={{ opacity: 0.85 }}>— {item.status}</span>}
            </div>
        ))}
    </div>
);

/**
 * Renders a comma-separated list of "<count> <status>" pairs (with a
 * colored dot per status) plus an optional "(Vn)" version label at the end.
 * Used by TaskCard and TaskRow to keep the status summary identical.
 *
 * For composed tasks, pass `breakdown` (array of `{ name, status }`) to
 * surface a per-sub-task hover tooltip — same elegant style as the
 * deployment badge breakdown (header + dot + name + status).
 */
export const TaskStatusSummary = ({ statuses, versionLabel, breakdown }) => {
    const safeStatuses = statuses || [];
    const statusText = safeStatuses
        .map(({ label, count }) => `${count} ${label}`)
        .join(', ');

    const hasBreakdown = Array.isArray(breakdown) && breakdown.length > 0;

    const inlineChildren = safeStatuses.map(({ label, count }, index) => (
        <span
            key={label}
            style={{
                display: 'inline',
                whiteSpace: 'nowrap',
            }}
        >
            {index > 0 ? ', ' : ''}
            <StatusDot status={label} />
            {count} {label}
        </span>
    ));

    // When a breakdown is present we render the rich tooltip ourselves and
    // skip `EllipsisTextWithTooltip` entirely to avoid stacking two tooltips
    // on the same target (the truncation tooltip would otherwise duplicate
    // information already shown in the breakdown header).
    const inlineContent = hasBreakdown ? (
        <div
            style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'help',
            }}
        >
            {inlineChildren}
        </div>
    ) : (
        <TruncatedText
            text={statusText}
            tooltipText={statusText}
            maxWidth="100%"
            style={{ flex: 1 }}
        >
            {inlineChildren}
        </TruncatedText>
    );

    return (
        <div
            style={{
                flex: 1,
                width: '100%',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
            }}
        >
            {hasBreakdown ? (
                <Tooltip
                    placement="top"
                    title={<StatusBreakdown summaryText={statusText} items={breakdown} />}
                >
                    {inlineContent}
                </Tooltip>
            ) : (
                inlineContent
            )}
            {versionLabel && (
                <span
                    style={{
                        flexShrink: 0,
                        marginLeft: 4,
                        whiteSpace: 'nowrap',
                    }}
                >
                    ({versionLabel})
                </span>
            )}
        </div>
    );
};

export default TaskStatusSummary;
